import { useApolloClient } from '@apollo/client';
import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Switch } from '@/components/form/baseInputs/Switch';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useSalesforceBrand } from '@/modules/salesforce/brand';
import { TenantInformationDocument } from '@/modules/tenant/graphql/TenantInformation.generated';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { UpdateIntegrationConfigurationInput } from '@/types/schema';
import { getPublicImageUrl } from '@/utils/staticFiles';

import {
  SalesforceConnectionDocument,
  SalesforceConnectionQuery,
  useConfigureSalesforceMutation,
  useSalesforceObjectsQuery,
  useSetSalesforceHouseholdObjectMutation,
  useSetSalesforceWealthHubBrandingMutation,
  useVerifySalesforceMutation,
} from './graphql/SalesforceIntegration.generated';
import {
  isSalesforceDomain,
  SalesforceConnection,
  salesforceError,
} from './salesforceApi';

export function SalesforceIntegrationCard({
  configurationID,
}: {
  configurationID: string;
}) {
  const client = useApolloClient();
  const brand = useSalesforceBrand();
  const [configure] = useConfigureSalesforceMutation({
    context: { sensitive: true },
    fetchPolicy: 'no-cache',
  });
  const [verify] = useVerifySalesforceMutation();
  const [connection, setConnection] = useState<SalesforceConnection | null>();
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [needsReload, setNeedsReload] = useState(false);

  async function refresh() {
    const { data } = await client.query<SalesforceConnectionQuery>({
      query: SalesforceConnectionDocument,
      fetchPolicy: 'no-cache',
    });
    setConnection(data.salesforceConnection ?? null);
    setNeedsReload(false);
  }

  useEffect(() => {
    let active = true;
    void client
      .query<SalesforceConnectionQuery>({
        query: SalesforceConnectionDocument,
        fetchPolicy: 'no-cache',
      })
      .then(({ data }) => {
        if (active) setConnection(data.salesforceConnection ?? null);
      })
      .catch((error: unknown) => {
        if (active) setError(salesforceError(error));
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [client]);

  async function run(action?: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await action?.();
      setNeedsReload(true);
      await refresh();
      return true;
    } catch (error) {
      setError(salesforceError(error));
      // Reload after a failure because a network error can hide a successful write.
      setNeedsReload(true);
      return false;
    } finally {
      setBusy(false);
    }
  }

  let label = 'Not configured';
  if (connection) label = 'Configured - Not connected';
  if (connection?.connected) label = 'Connected';

  return (
    <Stack spacing={2} aria-label="Salesforce integration" aria-busy={busy}>
      <Box textAlign="center">
        <Box
          component="img"
          alt={brand.name}
          src={getPublicImageUrl(brand.logoPath)}
          sx={{ height: 56, maxWidth: '100%', objectFit: 'contain' }}
        />
      </Box>
      <Typography variant="body-s">
        Connect your tenant’s Salesforce client app using its Run As integration
        user.
      </Typography>
      <WealthHubBrandingSetting
        configurationID={configurationID}
        disabled={busy}
      />
      {error && (
        <Callout severity="error">
          <span role="alert">{error}</span>
        </Callout>
      )}
      {busy && (
        <Typography variant="body-s" role="status">
          Updating Salesforce…
        </Typography>
      )}
      {(needsReload || connection === undefined) && !busy && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            void run();
          }}
        >
          Reload Salesforce settings
        </Button>
      )}
      {connection !== undefined && !editing && (
        <>
          <Typography variant="body-s" role="status">
            {label}
          </Typography>
          {connection && (
            <Typography variant="body-xs" sx={{ overflowWrap: 'anywhere' }}>
              {connection.domain}
            </Typography>
          )}
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || needsReload}
            onClick={() => setEditing(true)}
          >
            {connection ? 'Edit configuration' : 'Configure Salesforce'}
          </Button>
          {connection && (
            <Button
              size="sm"
              variant="primary"
              disabled={busy || needsReload}
              onClick={() => {
                void run(() => verify());
              }}
            >
              {connection.connected
                ? 'Verify connection'
                : 'Connect integration'}
            </Button>
          )}
        </>
      )}
      {editing && (
        <SalesforceConfigurationForm
          connection={connection ?? null}
          disabled={busy || needsReload}
          onCancel={() => setEditing(false)}
          onSave={async (input) => {
            if (
              await run(() =>
                configure({
                  variables: { input: { id: configurationID, update: input } },
                })
              )
            )
              setEditing(false);
          }}
        />
      )}
      {connection?.connected && !editing && (
        <HouseholdObjectSetting
          connection={connection}
          disabled={busy || needsReload}
          run={run}
        />
      )}
    </Stack>
  );
}

/**
 * Switches the tenant between WealthHub and Salesforce branding. Every branded
 * surface reads the tenant configuration, so the switch reloads it and those
 * surfaces change without a page reload.
 */
function WealthHubBrandingSetting({
  configurationID,
  disabled,
}: {
  configurationID: string;
  disabled: boolean;
}) {
  const { integrationConfiguration } = useTenantDetailsContext();
  const [setBranding, { loading }] = useSetSalesforceWealthHubBrandingMutation({
    refetchQueries: [TenantInformationDocument],
    awaitRefetchQueries: true,
    // TenantInformation is excluded from automatic mutation refetches.
    onQueryUpdated: (query) => query.refetch(),
  });
  const [error, setError] = useState('');
  return (
    <Stack spacing={1}>
      <Switch
        label="Show as WealthHub"
        labelPosition="right"
        value={integrationConfiguration?.salesforceIsWealthhub ?? false}
        helpText="Users see the WealthHub name and logo for this integration. Salesforce URLs and object names do not change."
        disabled={disabled || loading || !integrationConfiguration}
        onChange={(event) => {
          setError('');
          setBranding({
            variables: {
              id: configurationID,
              isWealthHub: event.target.checked,
            },
          }).catch((failure: unknown) => setError(salesforceError(failure)));
        }}
      />
      {error && (
        <Callout severity="error">
          <span role="alert">{error}</span>
        </Callout>
      )}
    </Stack>
  );
}

function SalesforceConfigurationForm({
  connection,
  disabled,
  onCancel,
  onSave,
}: {
  connection: SalesforceConnection | null;
  disabled: boolean;
  onCancel: () => void;
  onSave: (input: UpdateIntegrationConfigurationInput) => Promise<void>;
}) {
  const [domain, setDomain] = useState(connection?.domain ?? '');
  const [key, setKey] = useState('');
  const [secret, setSecret] = useState('');
  const [submitted, setSubmitted] = useState(false);
  return (
    <Stack
      component="form"
      spacing={2}
      onSubmit={(event) => {
        event.preventDefault();
        setSubmitted(true);
        if (!isSalesforceDomain(domain) || !key.trim() || !secret) return;
        const input = {
          salesforceLoginDomain: domain.trim(),
          salesforceClientID: key.trim(),
          salesforceClientSecret: secret,
        };
        setKey('');
        setSecret('');
        setSubmitted(false);
        void onSave(input);
      }}
    >
      <TextInput
        autoFocus
        label="Salesforce login URL or domain"
        value={domain}
        disabled={disabled}
        helpText="Paste the Salesforce URL or domain that Salesforce provides, for example https://company.lightning.force.com."
        onChange={(event) => setDomain(event.target.value)}
        errorMessage={
          submitted && !isSalesforceDomain(domain)
            ? 'Enter a Salesforce HTTPS URL or domain without a port or path.'
            : undefined
        }
      />
      <TextInput
        label="Client ID (client key)"
        autoComplete="off"
        value={key}
        disabled={disabled}
        onChange={(event) => setKey(event.target.value)}
        errorMessage={
          submitted && !key.trim() ? 'Enter the client ID.' : undefined
        }
      />
      <TextInput
        label="Client secret"
        type="password"
        autoComplete="new-password"
        value={secret}
        disabled={disabled}
        onChange={(event) => setSecret(event.target.value)}
        errorMessage={
          submitted && !secret ? 'Enter the client secret.' : undefined
        }
      />
      <Typography variant="body-xs">
        Stored credentials are never shown. Enter both credentials for each
        save.
      </Typography>
      {connection && (
        <Callout severity="warning">
          Saving replaces the configuration. Verify the connection and choose
          the household object again.
        </Callout>
      )}
      <Stack direction="row" spacing={1}>
        <Button size="sm" variant="secondary" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" type="submit" disabled={disabled}>
          Save configuration
        </Button>
      </Stack>
    </Stack>
  );
}

function HouseholdObjectSetting({
  connection,
  disabled,
  run,
}: {
  connection: SalesforceConnection;
  disabled: boolean;
  run: (action: () => Promise<unknown>) => Promise<boolean>;
}) {
  const { data, error, refetch } = useSalesforceObjectsQuery({
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
  });
  const [setObject] = useSetSalesforceHouseholdObjectMutation();
  const objects = data?.salesforceObjects.filter((object) => object.queryable);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState('');
  const saved = objects?.find((object) => object.name === connection.object);
  let savedLabel = connection.object ?? 'Not configured';
  if (saved) savedLabel = `${saved.label} (${saved.name})`;
  return (
    <Stack spacing={1.5} component="section" aria-label="Household object">
      <Typography variant="title-xs" component="h3">
        Household object
      </Typography>
      <Typography
        variant="body-s"
        aria-label="Saved household object"
        sx={{ overflowWrap: 'anywhere' }}
      >
        {savedLabel}
      </Typography>
      {error && (
        <Typography variant="body-xs" role="alert">
          Unable to load Salesforce objects. Check integration permissions and
          try again.
        </Typography>
      )}
      {!objects && !error && (
        <Typography variant="body-xs" role="status">
          Loading Salesforce objects…
        </Typography>
      )}
      {error && (
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled}
          onClick={() => {
            void refetch().catch(() => undefined);
          }}
        >
          Retry objects
        </Button>
      )}
      {!editing && (
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled || !objects}
          onClick={() => setEditing(true)}
        >
          Choose household object
        </Button>
      )}
      {editing && objects && (
        <>
          {/* nosemgrep: typescript.react.portability.i18next.jsx-label-not-i18n.jsx-label-not-i18n -- Interface has no localization API; integration settings use English labels. */}
          <TextField
            select
            label="Salesforce household object"
            value={selected}
            disabled={disabled}
            onChange={(event) => setSelected(event.target.value)}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 320,
                    maxWidth: 'min(440px, calc(100vw - 32px))',
                  },
                },
              },
            }}
          >
            {objects.map((object) => (
              <MenuItem
                key={object.name}
                value={object.name}
                sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}
              >
                {object.label} ({object.name})
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="body-xs">
            Salesforce must permit queries and provide a searchable text Name
            field. Saving checks current metadata.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="secondary"
              disabled={disabled}
              onClick={() => {
                setEditing(false);
                setSelected('');
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={disabled || !selected}
              onClick={() => {
                void run(() =>
                  setObject({ variables: { object: selected } })
                ).then((saved) => {
                  if (saved) {
                    setEditing(false);
                    setSelected('');
                  }
                });
              }}
            >
              Save household object
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}
